import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from '../../../../utilities/appToast';
import { getLiquidacionporid } from '../../../../api/axios';
import BigSpinner from '../../../General/BigSpinner';
import '../../shared/liquidacionesModule.css';
import LiquidacionesPageHeader from '../../shared/LiquidacionesPageHeader';
import { LIQUIDACIONES_ROUTES } from '../../shared/liquidacionesRoutes';
import {
    buildLiquidacionPipelineMonitorModel,
    getLiquidacionPipelineSnapshot,
    getPipelineDisplaySummary,
    normalizePipelineStepProgress,
    PIPELINE_STATUS_LABELS,
    PIPELINE_STEP_STATUS_LABELS
} from '../../../../helper/liquidacionPipelineMonitorHelper';
import './LiquidacionPipelineDetalle.css';

const AUTHORIZED_VIEW_ALL = [1, 2, 8];

const normalizeLiquidacionDetalle = (value) => {
    const detail = value || {};

    return {
        ...detail,
        encabezadoLiquidacion: detail?.encabezadoLiquidacion ?? detail?.EncabezadoLiquidacion ?? {},
        cuerpoOrdenLiquidaciones: detail?.cuerpoOrdenLiquidaciones ?? detail?.CuerpoOrdenLiquidaciones ?? [],
        cuerpoNovedadLiquidaciones: detail?.cuerpoNovedadLiquidaciones ?? detail?.CuerpoNovedadLiquidaciones ?? [],
        cuerpoFeeLogisticoLiquidaciones: detail?.cuerpoFeeLogisticoLiquidaciones ?? detail?.CuerpoFeeLogisticoLiquidaciones ?? [],
        detalleRebate: detail?.detalleRebate ?? detail?.DetalleRebate ?? []
    };
};

const resolveSellerId = (...values) => {
    for (const value of values) {
        const parsedValue = Number.parseInt(value, 10);
        if (Number.isFinite(parsedValue) && parsedValue > 0) {
            return parsedValue;
        }
    }

    return undefined;
};

function ProgressRing({ progress, status, size = 'lg' }) {
    const value = Math.max(0, Math.min(100, progress));
    const ringStyle = {
        background: `conic-gradient(var(--ring-color) ${value * 3.6}deg, var(--ring-track) 0deg)`
    };

    return (
        <div
            className={`pipeline-detalle-ring pipeline-detalle-ring--${size} pipeline-detalle-ring--${status || 'pending'}`}
            style={ringStyle}
            role="img"
            aria-label={`${value}% completado`}
        >
            <div className="pipeline-detalle-ring__inner">
                <strong>{value}%</strong>
            </div>
        </div>
    );
}

function StepProgressCard({ step, index }) {
    const progress = normalizePipelineStepProgress(step);
    const status = step?.status || 'pending';

    return (
        <article className={`pipeline-detalle-step pipeline-detalle-step--${status}`}>
            <div className="pipeline-detalle-step__header">
                <div className="pipeline-detalle-step__heading">
                    <span className="pipeline-detalle-step__index">{String(index + 1).padStart(2, '0')}</span>
                    <div>
                        <h3>{step.label}</h3>
                        {step.description && <p className="pipeline-detalle-step__description">{step.description}</p>}
                    </div>
                </div>
                <ProgressRing progress={progress} status={status} size="md" />
            </div>

            <div className="pipeline-detalle-step__status-row">
                <span className={`pipeline-detalle-step__pill pipeline-detalle-step__pill--${status}`}>
                    {PIPELINE_STEP_STATUS_LABELS[status] || PIPELINE_STEP_STATUS_LABELS.pending}
                </span>
                <div className="pipeline-detalle-step__bar">
                    <div
                        className={`pipeline-detalle-step__bar-fill pipeline-detalle-step__bar-fill--${status}`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {Array.isArray(step.meta) && step.meta.length > 0 && (
                <ul className="pipeline-detalle-step__meta">
                    {step.meta.filter(Boolean).map((item, itemIndex) => (
                        <li key={`${step.key || step.label}-meta-${itemIndex}`}>{item}</li>
                    ))}
                </ul>
            )}

            {step.note && (
                <div className="pipeline-detalle-step__note">{step.note}</div>
            )}

            {step.error && (
                <div className="pipeline-detalle-step__error" role="alert">{step.error}</div>
            )}
        </article>
    );
}

function LiquidacionPipelineDetalle({ tipoUsuario, idSeller }) {
    const navigate = useNavigate();
    const { idLiquidacion: idLiquidacionParam } = useParams();
    const location = useLocation();
    const [detalle, setDetalle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isAuthorizedViewAll = AUTHORIZED_VIEW_ALL.includes(tipoUsuario);

    const idLiquidacion = Number.parseInt(idLiquidacionParam, 10);
    const navigationState = location.state || {};
    const stateIdSeller = navigationState.idSeller ?? navigationState.IdSeller;
    const stateBorrador = navigationState.borrador;
    const stateTipoEstado = navigationState.idTipoEstadoLiquidacion;
    const stateRazonSocial = navigationState.razonSocialSeller;
    const stateMostrarFecha = navigationState.mostrarFecha;
    const stateCantidadOrdenes = navigationState.cantidadOrdenes;
    const stateCantidadNovedades = navigationState.cantidadNovedades;

    useEffect(() => {
        if (!Number.isFinite(idLiquidacion) || idLiquidacion < 1) {
            toast.error('Identificador de liquidacion invalido.');
            navigate(LIQUIDACIONES_ROUTES.pipelines, { replace: true });
            return;
        }

        const cargarDetalle = async () => {
            setIsLoading(true);

            try {
                const borrador = stateBorrador != null
                    ? Number(stateBorrador)
                    : (stateTipoEstado === 2 ? 1 : 0);

                const request = {
                    idLiquidacion,
                    borrador: borrador ? 1 : 0
                };

                const resolvedSellerId = resolveSellerId(
                    stateIdSeller,
                    !isAuthorizedViewAll ? idSeller : undefined
                );

                if (resolvedSellerId) {
                    request.idSeller = resolvedSellerId;
                }

                const response = await getLiquidacionporid(request);
                setDetalle(normalizeLiquidacionDetalle(response));
            } catch (error) {
                toast.error('No pudimos cargar el detalle del pipeline.');
                navigate(LIQUIDACIONES_ROUTES.pipelines, { replace: true });
            } finally {
                setIsLoading(false);
            }
        };

        cargarDetalle();
    }, [idLiquidacion, idSeller, isAuthorizedViewAll, navigate, stateBorrador, stateIdSeller, stateTipoEstado]);

    const pipeline = useMemo(() => {
        if (!detalle) {
            return null;
        }

        const encabezado = detalle.encabezadoLiquidacion || {};
        const snapshot = getLiquidacionPipelineSnapshot(idLiquidacion);
        const liquidacionCompleta = {
            ...encabezado,
            ...detalle,
            idLiquidacion,
            idSeller: resolveSellerId(
                stateIdSeller,
                encabezado?.idSeller,
                encabezado?.IdSeller,
                detalle?.idSeller,
                detalle?.IdSeller,
                !isAuthorizedViewAll ? idSeller : undefined
            ),
            razonSocialSeller: stateRazonSocial || encabezado.razonSocialSeller,
            mostrarFecha: stateMostrarFecha || encabezado.mostrarFecha,
            cantidadOrdenes: stateCantidadOrdenes ?? encabezado.cantidadOrdenes ?? detalle.cuerpoOrdenLiquidaciones?.length,
            cantidadNovedades: stateCantidadNovedades ?? encabezado.cantidadNovedades ?? detalle.cuerpoNovedadLiquidaciones?.length,
            idTipoEstadoLiquidacion: stateTipoEstado ?? encabezado.idTipoEstadoLiquidacion ?? detalle.idTipoEstadoLiquidacion
        };

        return buildLiquidacionPipelineMonitorModel(liquidacionCompleta, snapshot);
    }, [detalle, idLiquidacion, idSeller, isAuthorizedViewAll, stateCantidadNovedades, stateCantidadOrdenes, stateIdSeller, stateMostrarFecha, stateRazonSocial, stateTipoEstado]);

    const summary = pipeline ? getPipelineDisplaySummary(pipeline) : null;

    const handleVolver = () => {
        navigate(LIQUIDACIONES_ROUTES.pipelines, { replace: true });
    };

    if (isLoading) {
        return (
            <div className="container pipeline-detalle-page liquidaciones-modulo">
                <LiquidacionesPageHeader
                    title="Detalle del pipeline"
                    subtitle="Cargando información de la liquidación..."
                    onBack={handleVolver}
                    backLabel="Volver a pipelines"
                    showBack
                />
                <BigSpinner message="Cargando detalle del pipeline..." />
            </div>
        );
    }

    if (!pipeline) {
        return null;
    }

    return (
        <div className="container pipeline-detalle-page liquidaciones-modulo">
            <LiquidacionesPageHeader
                title={pipeline.title}
                subtitle={pipeline.subtitle}
                onBack={handleVolver}
                backLabel="Volver a pipelines"
                showBack
            />

            <header className="pipeline-detalle-hero">
                <div className="pipeline-detalle-hero__copy">
                    <span className="pipeline-detalle-hero__eyebrow">{pipeline.eyebrow}</span>
                    <h2 className="pipeline-detalle-hero__title">{pipeline.title}</h2>
                    {pipeline.subtitle && <p className="pipeline-detalle-hero__subtitle">{pipeline.subtitle}</p>}
                    <div className="pipeline-detalle-hero__chips">
                        <span className={`pipeline-detalle-hero__pill pipeline-detalle-hero__pill--${pipeline.overallStatus}`}>
                            {PIPELINE_STATUS_LABELS[pipeline.overallStatus] || summary.statusLabel}
                        </span>
                        <span className="pipeline-detalle-hero__chip">
                            {summary.completedCount} de {summary.totalCount} etapas completadas
                        </span>
                        {pipeline.updatedAtLabel && (
                            <span className="pipeline-detalle-hero__chip">Ultimo movimiento: {pipeline.updatedAtLabel}</span>
                        )}
                    </div>
                </div>

                <div className="pipeline-detalle-hero__summary-card">
                    <ProgressRing progress={summary.overallProgress} status={pipeline.overallStatus} size="lg" />
                    <div className="pipeline-detalle-hero__summary-copy">
                        <span>Avance general</span>
                        <strong>{summary.overallProgress}%</strong>
                        <small>{summary.statusLabel}</small>
                    </div>
                </div>
            </header>

            {pipeline.errorMessage && (
                <div className="pipeline-detalle-error" role="alert">
                    <strong>Se detecto un error en el pipeline.</strong>
                    <span>{pipeline.errorMessage}</span>
                </div>
            )}

            <section className="pipeline-detalle-steps">
                <div className="pipeline-detalle-steps__header">
                    <h3>Etapas del circuito</h3>
                    <p>Seguimiento detallado de ordenes, estructura, calculo fiscal, comprobantes y confirmacion.</p>
                </div>

                <div className="pipeline-detalle-steps__grid">
                    {pipeline.steps.map((step, index) => (
                        <StepProgressCard key={step.key || step.label} step={step} index={index} />
                    ))}
                </div>
            </section>

            {pipeline.footerNote && (
                <p className="pipeline-detalle-footer-note">{pipeline.footerNote}</p>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    tipoUsuario: state.general.tipoUsuario,
    idSeller: state.general.idSeller
});

export default connect(mapStateToProps)(LiquidacionPipelineDetalle);
